import { Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { buyStock, fetchWatchlist, removeFromWatchlist, sellHolding, setNote, updateHolding } from '../api';
import { WatchlistItem, WatchlistResponse } from '../types';
import GuideCard from './GuideCard';
import SearchBar from './SearchBar';
import WatchlistTable from './WatchlistTable';

// 🎨 규격 V2(08-23) 공용 부품 — 버튼은 고스트/켜짐 두 가지, 섹션 제목은 키커.
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';

interface Props {
  onSelectTicker: (ticker: string, name: string) => void;
}

export default function ScannerPage({ onSelectTicker }: Props) {
  const [data, setData] = useState<WatchlistResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingTicker, setDeletingTicker] = useState<string | null>(null);
  const [changed, setChanged] = useState<Set<string>>(new Set());   // ⑤ 지난 방문 이후 시그널 바뀐 종목
  useEffect(() => {
    const items = data?.items;
    if (!items || !items.length) return;
    let prev: Record<string, string> = {};
    try { prev = JSON.parse(localStorage.getItem('watch_last_sig') || '{}'); } catch { /* noop */ }
    const ch = new Set<string>(), cur: Record<string, string> = {};
    for (const it of items) { cur[it.ticker] = it.signal; if (prev[it.ticker] !== undefined && prev[it.ticker] !== it.signal) ch.add(it.ticker); }
    try { localStorage.setItem('watch_last_sig', JSON.stringify(cur)); } catch { /* noop */ }
    setChanged(ch);
  }, [data]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchWatchlist();
      setData(result);
    } catch {
      setError('데이터 로딩 실패. 백엔드 서버를 확인하세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (ticker: string) => {
    setDeletingTicker(ticker);
    try {
      await removeFromWatchlist(ticker);
      // 로컬 상태에서 즉시 제거 후 재분석
      setData((prev) =>
        prev ? { ...prev, items: prev.items.filter((i) => i.ticker !== ticker), total: prev.total - 1 } : prev
      );
      // 백그라운드에서 전체 재로드 (순서/점수 반영)
      load();
    } catch {
      // silent
    } finally {
      setDeletingTicker(null);
    }
  };

  const handleRowClick = (item: WatchlistItem) => {
    onSelectTicker(item.ticker, item.name);
  };

  const handleEditHolding = async (ticker: string, avgPrice: number, quantity: number) => {
    try {
      await updateHolding(ticker, avgPrice, quantity);
      load();   // 손익 재계산 위해 재로드
    } catch {
      // silent
    }
  };

  const handleSellHolding = async (ticker: string, sellPrice: number, quantity: number) => {
    try {
      await sellHolding(ticker, sellPrice, quantity);
      load();   // 보유 차감/정리 반영
    } catch {
      // silent
    }
  };

  const handleBuyHolding = async (ticker: string, buyPrice: number, quantity: number) => {
    try {
      await buyStock(ticker, buyPrice, quantity);
      load();   // 현금 차감 + 보유 반영
    } catch {
      // silent
    }
  };

  const handleSetNote = async (ticker: string, note: string) => {
    try {
      await setNote(ticker, note);
      load();   // 메모 반영
    } catch {
      // silent
    }
  };

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
      {/* 컨트롤 바 */}
      <div className="px-4 py-3 border-b border-dc-hair flex flex-wrap items-center gap-3">
        <SearchBar onAdded={load} />

        <div className="flex items-center gap-3 ml-auto">
          <span className="text-dc-ink2 text-[13px] whitespace-nowrap">
            관심종목{' '}
            <span className="text-dc-ink1 font-bold">{data?.total ?? '─'}</span>개
          </span>
          {data?.scan_time && (
            <span className="text-dc-ink3 text-[11.5px]">
              {new Date(data.scan_time).toLocaleTimeString('ko-KR')}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className={GHOST}
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            재분석
          </button>
        </div>
      </div>

      {/* 분석 중 배너 */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-3 border-b border-dc-hair bg-dc-bg0/60">
          <Loader2 size={13} className="animate-spin text-dc-point" />
          <span className="text-dc-ink2 text-xs">관심종목 분석 중...</span>
        </div>
      )}

      {/* 에러 */}
      {error && !loading && (
        <div className="px-4 py-3 border-b border-dc-warn/30 bg-dc-warn/[0.07] text-dc-warn text-xs">
          {error}
        </div>
      )}

      {/* 테이블 */}
      <div className="flex-1 overflow-auto">
        <GuideCard id="watchlist" title="관심종목 사용법">
          <p className="mb-1.5"><b className="text-dc-ink1">이 화면은?</b> 내가 담은 종목들의 <b>추세 점수·시그널</b>을 보고, 보유 손익까지 관리해요.</p>
          <p className="mb-1.5"><b className="text-dc-ink1">점수</b> — 100에 가까울수록 강한 상승추세(Minervini 8조건). <b className="text-dc-ink1">시그널</b> — 추세가 매우 강함·짧음 등 현재 국면.</p>
          <p><b className="text-dc-ink1">이렇게 쓰세요:</b> ① 위 검색창에서 종목 추가 → ② 점수·시그널로 추세 강도 확인 → ③ ✏️보유 입력·🟢매수·🔴매도로 손익 기록 → ④ 종목 클릭하면 <b>AI 심층분석</b>(행동 결론·진입가·손절가)이 떠요.</p>
        </GuideCard>
        {(() => {
          const items = data?.items ?? [];
          const held = items.filter((i) => (i.avg_price ?? 0) > 0);   // 💼 보유(평단 입력)
          const watch = items.filter((i) => !((i.avg_price ?? 0) > 0)); // 👀 관심(평단 미입력)
          const common = { loading: loading && !data, onRowClick: handleRowClick, onDelete: handleDelete, onEditHolding: handleEditHolding, onSellHolding: handleSellHolding, onBuyHolding: handleBuyHolding, onSetNote: handleSetNote, deletingTicker, changed };
          return (
            <>
              {/* ④ 보유(지킬 것) / 관심(노릴 것) 섹션 분리 — 질문이 다르면 상도 다르게 */}
              {held.length > 0 && (
                <>
                  <p className={`px-4 pt-4 pb-2 ${KICKER}`}>💼 보유 <span className="text-dc-ink3/70 font-medium">{held.length} · 손익 관리</span></p>
                  <WatchlistTable data={held} {...common} />
                </>
              )}
              <p className={`px-4 pt-4 pb-2 ${KICKER}`}>👀 관심 <span className="text-dc-ink3/70 font-medium">{watch.length} · 진입 타이밍</span></p>
              <WatchlistTable data={watch} {...common} />
            </>
          );
        })()}
      </div>
    </div>
  );
}
